// ─────────────────────────────────────────────────────────────────────────────
// T239 — FIRST-PARTY TELEMETRY, CLIENT HALF.
//
// What this is for, in two sentences the card states plainly:
//   ① production errors must reach the system BEFORE they reach the owner's
//      eyes — today a white screen in a visitor's browser leaves no trace at all;
//   ② the funnel must be answerable — how many start the assessment, where they
//      leave, how many ask for the summary, how many book.
//
// What it is NOT: an analytics product. There is no third party, no cookie, no
// cross-session identifier, no advertising id, no device fingerprint and no page
// content. It posts a fixed list of ten event names plus a whitelist of typed
// props to ONE endpoint on our own domain's Supabase project.
//
// ── THE FOUR SILENCES (every one of them a mutation target) ─────────────────
//   1. `navigator.doNotTrack === '1'` (or the legacy `window.doNotTrack`) — silent.
//   2. `navigator.globalPrivacyControl === true` (GPC) — silent.
//   3. `VITE_PUBLIC_TELEMETRY=off` — the master switch, silent, no rebuild of
//      this file needed to turn the whole thing off in production.
//   4. No `crypto.randomUUID` / no `sessionStorage` — silent, because without a
//      session id there is no throttle key and the server would refuse anyway.
// Silence means SILENT: no queue, no beacon, no console noise, no retry.
//
// ── THE SESSION ID ──────────────────────────────────────────────────────────
// `crypto.randomUUID()` with the dashes removed, kept in **sessionStorage**.
// sessionStorage dies with the tab, so two visits by the same person are two
// unrelatable sessions by construction — the funnel can count a journey and can
// never follow a person. Nothing derived from the visitor (no IP, no UA, no
// canvas, no fonts) contributes to it; it is 128 bits of randomness and nothing
// else, which is why it can be a throttle key without being an identity.
//
// ── SAMPLING ────────────────────────────────────────────────────────────────
// Funnel events and errors are sent at 100% (they are rare and each one carries
// meaning). `page_view` and `web_vital` are sampled at 25% — but PER SESSION,
// derived deterministically from the session id, not per event. A per-event coin
// flip would give a session 3 of its 12 page views and make a path through the
// site unreadable; a per-session decision keeps whole journeys intact and simply
// keeps a quarter of them. It is also testable without a clock or a random
// source, which a per-event flip is not.
//
// ── SHARED POLICY, NOT A SECOND COPY ────────────────────────────────────────
// The event list, the prop whitelist and the scrubber are imported from the
// endpoint's own module. There is exactly one list in this repo; if this file
// disagreed with the server the server would win silently and the funnel would
// under-count with nothing going red.
// ─────────────────────────────────────────────────────────────────────────────
import {
  EVENTS,
  MAX_BATCH,
  isAllowedEvent,
  sanitizeProps,
  scrubText,
  MAX_ERROR_TEXT,
  type TelemetryEvent,
} from '../../supabase/functions/collect-events/policy.ts';

export { EVENTS, type TelemetryEvent };

/** Where the queue is posted. One endpoint, our own project, no third party. */
export const ENDPOINT_PATH = '/functions/v1/collect-events';

/** sessionStorage key for the per-tab id. */
export const SESSION_KEY = 'tf_tel_sid';

/** Percentage of SESSIONS whose page_view / web_vital events are sent. */
export const SAMPLE_PCT = 25;

/** The events sampling applies to; everything else is 100%. */
export const SAMPLED_EVENTS: readonly TelemetryEvent[] = ['page_view', 'web_vital'];

/** Flush cadence for the queue while the tab is alive. */
export const FLUSH_INTERVAL_MS = 5000;

// ── The decision core — pure functions, no globals, no I/O ──────────────────
// Everything above the wiring section is a pure function of its arguments so
// the gate can drive it under plain node. The runtime half below is thin on
// purpose: it reads the browser, calls these, and posts.

export interface PrivacySignals {
  /** navigator.doNotTrack / window.doNotTrack, verbatim. */
  dnt?: string | null;
  /** navigator.globalPrivacyControl. */
  gpc?: boolean | null;
  /** VITE_PUBLIC_TELEMETRY, verbatim. */
  switchValue?: string | null;
}

/**
 * The one function that decides whether this module does anything at all.
 *
 * DNT is honoured on `'1'` and on `'yes'` (the value Firefox used to send).
 * `'0'` and `'unspecified'` are NOT a signal — they mean the visitor either
 * declined the preference or never expressed one.
 *
 * The master switch is off for `'off'`, `'0'` and `'false'`, case-insensitively,
 * because an ops switch that only answers to one exact spelling is a switch that
 * will one day be set and not work.
 */
export function isSilenced(signals: PrivacySignals): boolean {
  const dnt = (signals.dnt ?? '').toString().trim().toLowerCase();
  if (dnt === '1' || dnt === 'yes') return true;
  if (signals.gpc === true) return true;
  const sw = (signals.switchValue ?? '').toString().trim().toLowerCase();
  if (sw === 'off' || sw === '0' || sw === 'false') return true;
  return false;
}

/** A uuid (or any string) reduced to the server's 32-lowercase-hex shape. */
export function toSessionHash(uuid: string): string {
  const hex = uuid.toLowerCase().replace(/[^0-9a-f]/g, '');
  return hex.slice(0, 32);
}

/**
 * This session's sampling bucket, 0–99, read from the id's first 8 hex digits.
 * Deterministic: the same session always lands in the same bucket, so a journey
 * is never half-recorded.
 */
export function sessionBucket(sessionHash: string): number {
  const n = parseInt(sessionHash.slice(0, 8), 16);
  return Number.isFinite(n) ? n % 100 : 0;
}

/** Is this event sent for this session? Funnel + errors always; the rest sampled. */
export function shouldSample(event: TelemetryEvent, sessionHash: string): boolean {
  if (!SAMPLED_EVENTS.includes(event)) return true;
  return sessionBucket(sessionHash) < SAMPLE_PCT;
}

/**
 * The booking funnel, inferred from the URL alone.
 *
 * `src/pages/book-consultation/**` is a FROZEN write-face (the payment chain,
 * T233): this card may not add an emit call inside it, so the three booking
 * events are read off the route the router already puts in the address bar.
 *
 * ⚠ HONEST LIMIT, recorded on the card and in the PR rather than papered over:
 * the booking page keeps its step in React state and writes `?step=` to the URL
 * only on the Stripe CANCEL return (`cancelUrl` → `/book-consultation?step=3`).
 * So `booking_payment_open` here means "the visitor reached the payment step and
 * came back", not "checkout opened". It is a real, useful signal — a returned
 * cancel is exactly the drop-off worth knowing about — but it under-counts
 * checkout opens, and closing that gap needs one emit line inside the frozen
 * page, which is a separate card's decision to make.
 */
export function inferBookingEvent(path: string, search: string): TelemetryEvent | null {
  const p = path.replace(/\/+$/, '') || '/';
  if (p.endsWith('/booking-success')) return 'booking_paid_return';
  if (p.endsWith('/book-consultation')) {
    const step = new URLSearchParams(search).get('step');
    return step === '3' ? 'booking_payment_open' : 'booking_start';
  }
  return null;
}

/** Dedupe key for an error: the same message is reported once per session. */
export function errorKey(message: string): string {
  return message.slice(0, 120);
}

// ── The runtime half ────────────────────────────────────────────────────────

interface QueuedEvent {
  event: TelemetryEvent;
  path: string | null;
  props: Record<string, string | number>;
}

interface Wiring {
  sessionHash: string;
  endpoint: string;
  lang: 'en' | 'zh' | null;
  queue: QueuedEvent[];
  seenErrors: Set<string>;
  timer: ReturnType<typeof setTimeout> | null;
  lastPageKey: string;
  lastBookingEvent: TelemetryEvent | null;
}

/** null until initTelemetry() decides telemetry runs at all. Silence = null. */
let wiring: Wiring | null = null;
let initialised = false;

/**
 * The build-time values this module needs, read once and injectable.
 *
 * `import.meta.env` is vite's, so it does not exist under the node gate that
 * drives this file; passing the values in is the `src/intake/mrz/flags.ts`
 * precedent — the production call site takes the default and reads vite, and a
 * gate hands in what it wants to test. Nothing branches on being under test.
 */
export interface TelemetryEnv {
  supabaseUrl: string | null;
  telemetrySwitch: string | null;
}

function envValue(key: string): string | null {
  try {
    const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
    return env?.[key] ?? null;
  } catch {
    return null;
  }
}

export function readTelemetryEnv(): TelemetryEnv {
  return {
    supabaseUrl: envValue('VITE_PUBLIC_SUPABASE_URL'),
    telemetrySwitch: envValue('VITE_PUBLIC_TELEMETRY'),
  };
}

function currentLang(): 'en' | 'zh' | null {
  try {
    return document.documentElement.lang?.startsWith('zh') ? 'zh' : 'en';
  } catch {
    return null;
  }
}

/** The per-tab id, minted on first use. Returns '' when the browser gives us no
 *  storage or no randomness — which is one of the four silences. */
function readSessionHash(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing && existing.length === 32) return existing;
    if (typeof crypto?.randomUUID !== 'function') return '';
    const fresh = toSessionHash(crypto.randomUUID());
    if (fresh.length !== 32) return '';
    sessionStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  } catch {
    return '';
  }
}

function flush(): void {
  const w = wiring;
  if (!w || w.queue.length === 0) return;
  const batch = w.queue.splice(0, MAX_BATCH);
  const body = JSON.stringify({
    session_hash: w.sessionHash,
    lang: w.lang,
    events: batch,
  });
  try {
    // sendBeacon first: it survives the page being torn down, which is exactly
    // when the last funnel event of a visit happens. A JSON blob so the function
    // sees the content-type it parses.
    const beacon = navigator?.sendBeacon;
    if (typeof beacon === 'function') {
      const ok = navigator.sendBeacon(w.endpoint, new Blob([body], { type: 'application/json' }));
      if (ok) return;
    }
    // Fallback: keepalive fetch. Never awaited, never surfaced — a telemetry
    // failure must not become a visible error, least of all inside the error
    // handler that produced it.
    void fetch(w.endpoint, {
      method: 'POST',
      keepalive: true,
      headers: { 'content-type': 'application/json' },
      body,
    }).catch(() => {});
  } catch {
    /* silent by design */
  }
}

function scheduleFlush(): void {
  const w = wiring;
  if (!w || w.timer !== null) return;
  w.timer = setTimeout(() => {
    if (wiring) wiring.timer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

/**
 * Record one event. Safe to call from anywhere, including before init and
 * inside an error handler: it never throws, never blocks, never awaits.
 *
 * The props go through the SERVER's own sanitiser before they are queued, so a
 * key that would be dropped at the door never travels at all.
 */
export function emit(event: TelemetryEvent, props: Record<string, unknown> = {}): void {
  try {
    const w = wiring;
    if (!w) return;
    if (!isAllowedEvent(event)) return;
    if (!shouldSample(event, w.sessionHash)) return;
    const path = typeof location !== 'undefined' ? location.pathname : null;
    w.queue.push({ event, path, props: sanitizeProps(event, props).props });
    if (w.queue.length >= MAX_BATCH) flush();
    else scheduleFlush();
  } catch {
    /* telemetry never breaks the page it measures */
  }
}

function reportError(message: unknown, stack?: unknown): void {
  const w = wiring;
  if (!w) return;
  const msg = scrubText(typeof message === 'string' ? message : String(message ?? ''), MAX_ERROR_TEXT);
  if (!msg) return;
  const key = errorKey(msg);
  if (w.seenErrors.has(key)) return;   // once per session per message
  w.seenErrors.add(key);
  emit('js_error', { message: msg, stack: scrubText(stack, MAX_ERROR_TEXT) ?? undefined });
  flush();                              // errors do not wait for the timer
}

function handleRoute(): void {
  const w = wiring;
  if (!w) return;
  const path = location.pathname;
  const search = location.search;
  if (path !== w.lastPageKey) {
    w.lastPageKey = path;
    w.lang = currentLang();
    emit('page_view');
  }
  // The booking funnel rides the route, because its page is frozen (see
  // inferBookingEvent). Each stage fires once per session.
  const booking = inferBookingEvent(path, search);
  if (booking && booking !== w.lastBookingEvent) {
    w.lastBookingEvent = booking;
    emit(booking);
  }
}

function observeWebVitals(): void {
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (nav && Number.isFinite(nav.responseStart)) {
      emit('web_vital', { metric: 'TTFB', value: Math.round(nav.responseStart) });
    }
    if (typeof PerformanceObserver !== 'function') return;

    let lcp = 0;
    const lcpObs = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) lcp = Math.max(lcp, entry.startTime);
    });
    lcpObs.observe({ type: 'largest-contentful-paint', buffered: true });

    let cls = 0;
    const clsObs = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const shift = entry as PerformanceEntry & { value?: number; hadRecentInput?: boolean };
        if (!shift.hadRecentInput && typeof shift.value === 'number') cls += shift.value;
      }
    });
    clsObs.observe({ type: 'layout-shift', buffered: true });

    // Report once, when the page goes away — the only moment either number is final.
    addEventListener('pagehide', () => {
      if (lcp > 0) emit('web_vital', { metric: 'LCP', value: Math.round(lcp) });
      if (cls > 0) emit('web_vital', { metric: 'CLS', value: Math.round(cls * 1000) / 1000 });
      flush();
    }, { once: true });
  } catch {
    /* a browser without these observers simply reports no vitals */
  }
}

/**
 * Wire telemetry into the page. Called once from main.tsx. Returns true when
 * telemetry is live, false when one of the four silences applied — the return
 * value exists so the wiring is testable, not so anything branches on it.
 */
export function initTelemetry(env: TelemetryEnv = readTelemetryEnv()): boolean {
  if (initialised) return wiring !== null;
  initialised = true;
  try {
    if (typeof window === 'undefined' || typeof document === 'undefined') return false;

    const nav = navigator as Navigator & { globalPrivacyControl?: boolean; doNotTrack?: string };
    const silenced = isSilenced({
      dnt: nav.doNotTrack ?? (window as Window & { doNotTrack?: string }).doNotTrack ?? null,
      gpc: nav.globalPrivacyControl ?? null,
      switchValue: env.telemetrySwitch,
    });
    if (silenced) return false;

    const base = env.supabaseUrl;
    if (!base) return false;
    const sessionHash = readSessionHash();
    if (!sessionHash) return false;

    wiring = {
      sessionHash,
      endpoint: `${base.replace(/\/+$/, '')}${ENDPOINT_PATH}`,
      lang: currentLang(),
      queue: [],
      seenErrors: new Set<string>(),
      timer: null,
      lastPageKey: '',
      lastBookingEvent: null,
    };

    // ① errors — the reason this card exists. Both global sinks, deduped per
    //    session so one loop cannot fill the table.
    addEventListener('error', (e: ErrorEvent) => {
      reportError(e.message || e.error?.message, e.error?.stack);
    });
    addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
      const reason = e.reason as { message?: string; stack?: string } | string | undefined;
      if (typeof reason === 'string') reportError(reason);
      else reportError(reason?.message ?? 'unhandledrejection', reason?.stack);
    });

    // ② routes — SPA navigations do not fire a page load, so the two history
    //    methods react-router calls are wrapped. Wrapping (not replacing) keeps
    //    the router's own behaviour byte-identical; the wrapper only notifies.
    const history = window.history;
    const wrap = (name: 'pushState' | 'replaceState') => {
      const original = history[name].bind(history);
      history[name] = function patched(this: History, ...args: Parameters<History['pushState']>) {
        const result = original(...args);
        try { handleRoute(); } catch { /* never break navigation */ }
        return result;
      };
    };
    wrap('pushState');
    wrap('replaceState');
    addEventListener('popstate', () => handleRoute());

    // ③ the queue must not die with the tab.
    addEventListener('pagehide', () => flush());
    addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush();
    });

    handleRoute();
    observeWebVitals();
    return true;
  } catch {
    wiring = null;
    return false;
  }
}

/** Test seam: forget the wiring so a gate can re-init with different signals.
 *  Never called by the app. */
export function __resetTelemetryForTest(): void {
  if (wiring?.timer !== null && wiring?.timer !== undefined) clearTimeout(wiring.timer);
  wiring = null;
  initialised = false;
}

/** Test seam: the queue as it stands, without flushing it — the WHOLE queued row
 *  (a seam that hid a field would let a gate pass on a payload the server never
 *  actually receives). */
export function __telemetryQueueForTest(): ReadonlyArray<{ event: string; path: string | null; props: Record<string, string | number> }> {
  return wiring ? wiring.queue.map((q) => ({ event: q.event, path: q.path, props: q.props })) : [];
}
